import {
  CButton,
  CCol,
  CForm,
  CFormInput,
  CModal,
  CModalBody,
  CModalTitle,
  CRow,
} from '@coreui/react-pro'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PASSWORD_LENGTHS } from 'src/domain/auth/password'
import useAlert from 'src/hooks/alert/useAlert'
import useEscListener from 'src/hooks/events/useEscListener'
import useChangePasswordForm from 'src/hooks/forms/password/useChangePasswordForm'
import { login, updatePassword } from 'src/services/auth'
import ChangePasswordFormValues from 'src/types/forms/password/ChangePasswordFormValues'
import { apiUrlWithoutPrefix } from 'src/utils/api'
import { getEmailFromToken } from 'src/utils/commonUtils'
import { useRegion } from 'src/utils/context/RegionContext'
import { DEFAULT_ERROR_MSG, getErrorMessage } from 'src/utils/error'
import SnackbarAlert from '../SnackbarAlert/SnackbarAlert'
import Spacer from '../Spacer/Spacer'

interface ChangePasswordModalProps {
  open: boolean
  onClose: () => void
}

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  // Prevent ESC from closing the parent profile modal
  useEscListener({ shouldHandleEscape: open, onEscape: onClose })

  const { t } = useTranslation()
  const alert = useAlert()
  const queryClient = useQueryClient()
  const { connectedRegions } = useRegion()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useChangePasswordForm()

  const loginWithNewPassword = async (password: string) => {
    const email: string = getEmailFromToken()

    await login({ email, password, region: connectedRegions })

    // Ensure subsequent requests use the fresh token.
    await queryClient.invalidateQueries()
  }

  const handleClose = () => {
    changePasswordMutation.reset()
    resetForm()
    onClose()
  }

  const changePasswordMutation = useMutation({
    retry: false,
    mutationFn: async (data: ChangePasswordFormValues) => {
      await updatePassword({ currentPassword: data.currentPassword, newPassword: data.newPassword })

      // The API invalidates all existing tokens on password update, so we need to login again.
      await loginWithNewPassword(data.newPassword)
    },
    onSuccess: () => {
      alert.open({ message: t('profile.changePassword.success') })
      handleClose()
    },
    onError: (error: Error) => {
      alert.open({ message: error.message, severity: 'error' })
    },
  })

  const onSubmit = (data: ChangePasswordFormValues) => {
    changePasswordMutation.mutate(data)
  }

  const isPending = changePasswordMutation.isPending

  // Note: we're not using CPasswordInput because CoreUI seems to have a bug where the
  // invalid feedback isn't displayed, but CFormInput works fine, although it doesn't
  // provide the password visibility toggle.

  const currentPasswordEl = (
    <CRow>
      <CCol>
        <CFormInput
          label={t('profile.changePassword.form.currentPassword.label')}
          type="password"
          invalid={!!errors?.currentPassword}
          feedbackInvalid={errors?.currentPassword?.message}
          required
          {...register('currentPassword')}
          autoComplete="current-password"
        />
      </CCol>
    </CRow>
  )

  const requirementsEl = (
    <CRow>
      <div>
        <div>{t('password.requirements.title')}</div>
        <ul style={{ margin: 0 }}>
          {t('password.requirements.description', {
            minLength: PASSWORD_LENGTHS.MIN,
            maxLength: PASSWORD_LENGTHS.MAX,
          })
            .split('\\n')
            .filter((line) => line.trim())
            .map((line, index) => (
              <li key={index}>{line.trim()}</li>
            ))}
        </ul>
      </div>
    </CRow>
  )

  const newPasswordEl = (
    <CRow>
      <CCol xs={12}>
        <CFormInput
          label={t('profile.changePassword.form.newPassword.label')}
          type="password"
          invalid={!!errors?.newPassword}
          feedbackInvalid={errors?.newPassword?.message}
          required
          {...register('newPassword')}
          autoComplete="new-password"
        />
      </CCol>
      <CCol xs={12}>
        <CFormInput
          label={t('profile.changePassword.form.confirmPassword.label')}
          type="password"
          invalid={!!errors?.confirmPassword}
          feedbackInvalid={errors?.confirmPassword?.message}
          required
          {...register('confirmPassword')}
          autoComplete="new-password"
        />
      </CCol>
    </CRow>
  )

  const buttonsEl = (
    <CRow>
      <CCol className="hstack gap-2 justify-content-end">
        <CButton
          type="button"
          color="primary"
          variant="outline"
          onClick={handleClose}
          disabled={isPending}
        >
          {t('buttons.cancel')}
        </CButton>
        <CButton type="submit" color="primary" disabled={isPending}>
          {isPending
            ? t('profile.changePassword.form.submitting')
            : t('profile.changePassword.form.submit')}
        </CButton>
      </CCol>
    </CRow>
  )

  const titleEl = <CModalTitle>{t('profile.changePassword.title')}</CModalTitle>

  const formEl = (
    <CForm onSubmit={handleSubmit(onSubmit)}>
      <div className="vstack gap-3">
        {currentPasswordEl}
        {requirementsEl}
        {newPasswordEl}
        <Spacer />
        {buttonsEl}
      </div>
    </CForm>
  )

  return (
    <>
      <CModal visible={open} onClose={handleClose} alignment="center">
        <CModalBody className="vstack gap-4">
          {titleEl}
          {formEl}
        </CModalBody>
      </CModal>
      <SnackbarAlert useAlertReturn={alert} />
    </>
  )
}

export default ChangePasswordModal
